/**
 * HLS Controller - controls hls.js and the media element.
 */
'use strict';

import EventEmitter from 'eventemitter2';   // Enhanced Node style event emitter
import Event from './HlsEvents';
import State from './HlsStates';
import HlsError from './HlsErrors';
import { ErrorDetails as HlsJsError } from './hlsjs/errors';
import HlsErrorString from './HlsErrorStrings';
import PlaybackState from './PlaybackStates';
import MediaState from './MediaStates';

import EventHandlers from './EventHandlers';

import HlsJsController from './HlsJsController';
import MediaController from './MediaController';

import Utils from './HlsUtils';
import HlsMarkers from './HlsMarkers';
import InstanceCounter from './utils/instanceCounter';
import HlsKey from './utils/hlsKey';

//import type from 'type-detect';

import { logger, enableLogs } from './hlsjs/utils/logger';

let _counter = new InstanceCounter();
let _hlsKeyLoader = new HlsKey();

/**
* @access internal
*/
class HlsController {

//Creation
	constructor(config = {}, di = {}) {
		let disableWorkarounds = !!config.disableWorkarounds;
		this._disablePromiseOverloadCheck = !!config.disablePromiseOverloadCheck;
		let decryptOutsideWebworker = !!config.decryptOutsideWebworker;
		let instanceId = 0;

		//This needs to be incremented every time, but we do allow the value to be passed in as a config value.
		//Thus we check if it has been set and change the internal only if it has not been. Note that regardless
		//the _counter internal value is still incremented and the next creation will be valid unless
		//overridden again.
		instanceId = _counter.increment();
		if (this._instanceId === 0) {
			this._instanceId = instanceId;
		}

		this._emitter = di.eventEmitter || new EventEmitter();

		this._hlsJsController = di.hlsJsController || new HlsJsController( {
			disableWorkarounds,
			instanceId,
			decryptOutsideWebworker,
		}, {
			eventEmitter: this._emitter,
		});

		this._mediaController = di.mediaController || new MediaController( {
			disableWorkarounds,
			instanceId,
		}, {
			eventEmitter: this._emitter,
		});

		this._markers = new HlsMarkers(	{
			instanceId,
		}, {
			eventEmitter: this._emitter,
		});

		this._state = State.UNLOADED;
		this._playbackState = PlaybackState.NOTREADY;
		this._mediaState = MediaState.DETACHED;

		this._source = null;

		this._pendingMedia = null;
		this._media = null;

		this._paused = true;
		this._canplay = false;
		this._canplaythrough = false;
		this._ended = false;
		this._seeking = false;
		this._buffering = false;
		this._startPositionSet = false;
		this._startPosition = 0;
		this._canplayWaiting = false;
		this._instanceId = instanceId;

		//for now, STREAM_PLAYBACKRATE_CHANGED only fires in response to MEDIA_RATECHANGE, at which point this.playbackRate has already changed
		//so we need to keep track of the previous rate so we can emit it with the event
		this._lastPlaybackRate = 1;

		/*
		seek over hole workaround - IE generates extra seeking events on a bufferSeekOverHole error, which interferes
		with some seeking related processes. This workaround detects bufferSeekOverHole errors, and ignores the next
		seeking event that occurs.
		*/
		this._seekOverHoleWorkaround = Utils.isIE() && !disableWorkarounds;
		this._swallowNextSeek = false;

		/*
		canplay workaround - Firefox tends to fire media canplay and canplaythrough events later than other browsers,
		and in some cases may not fire the event at all. This workaround forces canplay to fire on the third fragment
		loaded (any earlier may interfere with startTime/startTimeByPdt), improving the time it takes to start.
		*/
		this._canplayWorkaround = Utils.isFirefox() && !disableWorkarounds;
		this._canplayWorkaroundCount = 0;

		/*
		Tizen 3 won't begin playback on audio only streams if startTime is used. This appears to be due to the timing of the seek, when hlsJs requests a seek
		to the start position, audio only streams on Tizen 3 just aren't ready to start yet. This works around the issue by delaying the initial seek and
		handling it in the media controller, which is set up to handle seeks when the media object isn't ready yet.
		*/
		this._lateStartWorkaround = Utils.isTizen3() && !disableWorkarounds;

		/*
		Tizen 2.3 crashes if we attempt to seek outside of the buffered range of an audio only stream. In this case, we need to finish loading the frag we're
		going to seek to first, then seek.
		*/
		this._audioOnlySeekWorkaround = Utils.isTizen2() && !disableWorkarounds;
		this._seekWaitingForBuffer = false; //also used for _dataReadySeekWorkaround

		/*
		IE11 on 8.1 crashes if we attempt to seek outside of the buffered range of some streams. In this case, we need to finish loading the frag we're
		going to seek to first, then seek. Will Investigate this further but for now it's always on.
		*/
		this._dataReadySeekWorkaround = Utils.isIEWin8Point1() && !disableWorkarounds;

		/*
		Tizen 2.3 has issues handling playback rate changes, it will always say it did the change, but will not always reflect this change in playback. To get
		the rate change to work consistently, a seek needs to be performed after the media element reports that it has done the change. This forces Tizen's
		media element to switch the playback rate to what it says it should be.
		*/
		this._forcePlaybackRateWorkaround = Utils.isTizen2() && !disableWorkarounds;

		/*
		Firefox (tested on 58.0.2) and Chrome (tested on 64.0.3282.167, issue replicates on Mac only) may desync on a playback rate change. This is the same workaround as the Tizen 2 playback rate workaround above,
		but only triggers when playback rate changes to 1.
		*/
		this._resyncPlaybackRateWorkaround = (Utils.isFirefox() || Utils.isChromiumMacOS()) && !disableWorkarounds;

		/*
		Added as it seemed that this was doing more harm than good on IEWin8Point1, but this does not seem to be the case. Removable. - SIEG
		*/
		this._disableFakeSeek = false; // Utils.isIEWin8Point1();

		this._seekBackOnPlay = false;

		this._initialTime = -1;
		this._pendingSeek = undefined;
		this._seekableRange = { start: undefined, end: undefined, duration: undefined };
		this._prevDuration = 0;
		this._playbackRange = { start: undefined, end: undefined, duration: undefined };

		this._playbackMetrics = {};

		this._debug = false;
		this._disposed = false;
		this._disposedPromise = undefined;

		this._loaded = false;
		this._fetching = false;
		this._autoPrefetch = true;
		this._autoPlay = false;
		this._autoRecover = true;
		this._prefetch = false;
		this._sourceLoaded = false;
		this._sourceMetadataLoaded = false;

		this._pendingUrl = null;
		this._pendingDispose = false;
		this._mediaErrorRecovery = false;
		this._playOnMediaErrorRecovery = false;
		this._recoveryOptions = { playbackRate: undefined, volume: undefined, startPdt: undefined };

		this._runtimeCheckFinished = false;

		this._handlers = new EventHandlers();

		this._handlers.setHlsEventsList(
			this,			// obj to bind to
			this._emitter,	// emitter to use
			[
				/* HlsController events */
				{ event: Event.MEDIA_WIRING,						func: this._onMediaWiring				},
				{ event: Event.MEDIA_WIRED,							func: this._onMediaWired 				},
				{ event: Event.MEDIA_ATTACHING,						func: this._onMediaAttaching			},
				{ event: Event.MEDIA_ATTACHED,						func: this._onMediaAttached				},
				{ event: Event.MEDIA_DETACHING,						func: this._onMediaDetaching			},
				{ event: Event.MEDIA_DETACHED,						func: this._onMediaDetached				},

				/* HlsJsController events */
				{ event: Event.HLSJS_ERROR,							func: this._onHlsJsError				},
				{ event: Event.MANIFEST_PARSED,						func: this._onManifestParsed			},
				{ event: Event.CHUNK_BUFFERED,						func: this._onChunkBuffered				},
				{ event: Event.STREAM_SUBTITLERENDITIONS_CHANGED,	func: this._onHlsJsSubtitleUpdate		},
				{ event: Event.VARIANT_DURATION_UPDATED,			func: this._onVariantDurationUpdate		},
				{ event: Event.STREAM_STARTPOSITION_SET,			func: this._onStartPositionSet			},
				{ event: Event.HLSJS_MEDIA_ATTACHED,				func: this._onHlsJsMediaAttached		},
				{ event: Event.HLSJS_MEDIA_DETACHED,				func: this._onHlsJsMediaDetached		},
				{ event: Event.STREAM_NETWORKMETRICS_CHANGED,		func: this._onNetworkMetricsChanged		},
				{ event: Event.HLSJS_SEEK_READY,					func: this._onHlsJsSeekReady			},

				/* MediaController events */
				{ event: Event.MEDIA_CONTROLLER_WIRED,				func: this._onMediaControllerWired		},
				{ event: Event.MEDIA_CONTROLLER_UNWIRED,			func: this._onMediaControllerUnwired	},

				{ event: Event.MEDIA_CANPLAY,						func: this._onMediaCanPlay				},
				{ event: Event.MEDIA_CANPLAYTHROUGH,				func: this._onMediaCanPlayThrough		},
				{ event: Event.MEDIA_DURATIONCHANGE,				func: this._onMediaDurationChange		},
				{ event: Event.MEDIA_TIMEUPDATE,					func: this._onMediaTimeUpdate			},
				{ event: Event.MEDIA_SEEKING,						func: this._onMediaSeeking				},
				{ event: Event.MEDIA_SEEKED,						func: this._onMediaSeeked				},
				{ event: Event.MEDIA_ENDED,							func: this._onMediaEnded				},
				{ event: Event.MEDIA_RATECHANGE,					func: this._onMediaRateChange			},
				{ event: Event.MEDIA_CONTROLLER_ERROR,				func: this._onMediaControllerError		},
				{ event: Event.MEDIA_ELEMENT_ERROR,					func: this._onMediaElementError			},

				/* internal test event */
				{ event: Event.TEST_EVENT,							func: this._onTestEvent					},
			]
		).wireHlsEvents();

		Utils.applyConfig(this, config);

		this._hlsJsController.keyCache(_hlsKeyLoader);
	}

	_promiseOverloadCheck() {
		if( !this._disablePromiseOverloadCheck ) {
			if(Utils.isPromiseOverloaded()) {
				//if Promise doesn't match native prototype, emit warning
				logger.warn('The native Promise object has been overloaded. This may cause issues, as various native APIs will return native Promises.');
				this._emitWarning( HlsJsError.PROMISE_IS_OVERLOADED );
			}
		}
		else {
			logger.log('Promise overload check has been disabled.');
		}
	}

	_runtimeCheck() {
		if(!this._runtimeCheckFinished) {
			//check if promise is overloaded

			this._promiseOverloadCheck();

			this._runtimeCheckFinished = true;
		}
	}

	_doDispose() {
		logger.log(`HlsController::_doDispose() instanceId ${this._instanceId}, instanceCount ${_counter.active()}`);

		if (!this._disposed) {
			this._disposed = true;

			// do not clear pendingDispose. Although we might schedule extra disposes, none of the extras will have effect,
			// because we'll have already disposed by the time they execute
		//	this._pendingDispose = false;

			logger.log(`STREAM_DISPOSING`);
			this._emit(Event.STREAM_DISPOSING);

			// no more video events please
			this._mediaController.dispose();
			//this._mediaController = null; //I wanted to null these out but there are too many assumptions. Should be fixed - SIEG
			this._hlsJsController.dispose();
			//this._hlsJsController = null; //I wanted to null these out but there are too many assumptions. Should be fixed - SIEG

			// start hlsjs destroy
			//FIXME: need to kill HlsJsController
			/*if ( this._hlsjs ) {
				this._unwireHlsJsEvents();
				this._hlsjs.destroy();
				this._hlsjs = null;
			}*/

			this._source = null;
			this._media = null;
			this._pendingMedia = null;

			this._STREAM_VARIANTS_CHANGED = [];
			this._sourceLoaded = false;
			this._sourceMetadataLoaded = false;

			this._swallowNextSeek = false;
			this._seekOverHoleWorkaround = false;

			this._seekableRange = null;
			this._playbackRange = null;
			this._playbackMetrics = null;

			this._markers.dispose();
			this._markers = null;

			// this should free up _emitter
			this._handlers.unwireHlsEvents();
			this._handlers.dispose();
			this._handlers = null;

			_counter.decrement(); //Can't do it after the event

			// this is the final EVENT that will ever be emitted
			logger.log(`STREAM_DISPOSED`);
			this._emit(Event.STREAM_DISPOSED);

			this._emitter.removeAllListeners();
			this._emitter = null;

			// this will destroy our event handlers too...
			this._mediaEvents = null;
			this._hlsJsEvents = null;

			// ITS NOW ILLEGAL TO USE HlsController.
		}
	}

	dispose() {
		logger.log('HlsController::dispose()');
		// we might have active stuff on the nextTick already.
		// the problem with doing this is that the outside app won't be sure
		// that we've disposed when we return... that was always going to be a
		// problem eventually though.
		//process.nextTick( () => this._doDispose() );

		if( !this._disposed) {
			// if we're detached and unloaded, then just dispose immediately
			// if we're loading HlsJs will blow up...
			if ( this._mediaState === MediaState.DETACHED && this._state === State.UNLOADED) {
				this._doDispose();
			}
			else if (!this._pendingDispose) {
				logger.log('HlsController::dispose - pending dispose after detach');
				// otherwise, if we're not already pendingDispose, then we need to pend this dispose
				// and detach. MEDIA_DETACHED will then trigger the pendingDispose to be resolved.
				this._pendingDispose = true;
				// unload
				this._doDetach();
			}
		}
		else {
			logger.log('HlsController::dispose - already disposed');
		}
	}

	static isSupported() {
		return Utils.isBrowser() && HlsJsController.isSupported();
	}

	static totalActive() {
		return _counter.active();
	}

	static totalCreated() {
		return _counter.total();
	}

	static isRedirectSupported() {
		//Current assumption is that IE and Tizen 2 are the only non-compliant platforms
		let redirectSupported = true;

		if (Utils.isIE() === true) {
			redirectSupported = false;
		}
		else if (Utils.isTizen2() === true) {
			redirectSupported = false;
		}

		return redirectSupported;
	}


	loadPendingSource() {
		logger.log('HlsController::loadPendingSource');
		/* TODO: do we reset in the event handler... or before running the event? */
		//this._source = url;
		if (this._state !== State.UNLOADED) {
			this._unloadSource();
		}

		//Should we do this? I've tested with and without. Need more testing - SIEG
		//this._resetState();
		//No. We should not as it breaks the way play works... but maybe pause should
		//be removed from it.

		this._loaded = false;
		let pendingUrl = this._pendingUrl;
		this._pendingUrl = null;

		this._beginStreamLoading(pendingUrl);
	}


	loadSource(url) {
		logger.log('HlsController::loadSource()');

		if( url !== null && !Utils.isString(url) ) {
			throw new Error( `loadSource: url ${url} must be either null, or a string`);
		}

		this._runtimeCheck();

		this._pendingUrl = null;

		//logger.log(this.isMediaAttached + ' ' + this._state);

		if( this._disposed ) {
			logger.log('loadSource() Disposed; cannot load');
		}
		else {
			this._pendingUrl = url;
			this._safeDoNextTick( () => this.doLoadSource());
		}
	}


	doLoadSource() {
		if (this._pendingUrl) {
			if ((this._mediaState === MediaState.ATTACHED || this._mediaState === MediaState.DETACHING) && this._state !== State.UNLOADED) {
				logger.log('doLoadSource() Need to detach first');
				if (this._media != null && this._mediaState !== MediaState.DETACHING) {
					this._pendingMedia = this._media;
					logger.log('HlsController::doLoadSource() old media stashed');
				}
				this._doDetach(); //This can take time. It is best to wait for the event.
			}
			else {
				logger.log('doLoadSource() loadPendingSource');
				this.loadPendingSource();
			}
		}
	}


	flushBuffer(levelFlush) {
		return this._hlsJsController.flushBuffer(!!levelFlush);
	}

	attachMedia(video) {
		logger.log(`HlsController::attachMedia() { video: ${video}, mediaState: ${this._mediaState} }`);

		/*
		if( video !== null && !(video instanceof HTMLMediaElement) ) {
			throw new TypeError( `HlsController::attachMedia(): { video: ${video} } must be an instanceof HTMLMediaElement, or null`);
		}
		*/

		// start by nullifying source.

		// it is a requirement that we nullify the src when the media is
		// attached, or if the source is set to null. Best to nullify it
		// before we loadSource.

		// It will be set to something once loadSource is called with a non-null
		// parameter.

		if( !this._disposed ) {

			//Moved to MediaController....

			//if( video && video.src && this._mediaState !== MediaState.DETACHING) {
			//	// its not as simple as setting the src to null.
			//	// https://github.com/w3c/media-source/issues/53
			//	video.removeAttribute('src');
			//	video.load();
			//}

			this._pendingMedia = video;

			if( this._mediaState !== MediaState.DETACHED && this._mediaState !== MediaState.DETACHING ) {
				logger.log( `HlsController::attachMedia, still attached, forcing detach, { mediaState: ${this._mediaState}, media: ${this._media} }`);
				this._doDetach();
			}
			else {
				// if we're ready to attach, don't wait for unwiring et al, just attach
				// the pendingMedia now.
				this._attachPendingMediaOrUrl();
			}
		}
		else {
			logger.log('attachMedia() Disposed; cannot attach');
		}
	}


	detachMedia() {
		logger.log('HlsController::detachMedia()');

		this._pendingMedia = null;

		this._doDetach();
	}

	_doDetach() {
		// this should trigger DETACHING and then DETACHED.
		// unless we're already DETACHED.
		logger.log(`HlsController::_doDetach() { mediaState: ${this._mediaState} }`);

		if( this._mediaState === MediaState.DETACHED ) {
			// if we're WIRED we never ATTACHED, if we're wiring, we never WIRED.
			// Either way, we can go straight to DETACHED and if necessary onMediaDetached
			// will take care of nulling the media.

			logger.log(`HlsController::_doDetach(), already DETACHED emitting MEDIA_DETACHED anyway`);
			// if we never attached, then we need to fake a MEDIA_DETACHED
			this._emit(Event.MEDIA_DETACHED, { media: null } );
		}
		else if( this._mediaState !== MediaState.DETACHING ) {
			// a state transition (ie from WIRING -> WIRED), may already be in flight, and if we don't
			// do this on the next tick, then we would be pre-empting it...

			//It's possible to nextTick several DETACHINGS. Not sure if this is a problem - SIEG
			this._safeDoNextTick( () => this._emit(Event.MEDIA_DETACHING, { media: null } ) );
		}
		else {
			logger.log('HlsController::_doDetach() not requesting detach');
			logger.log(this._mediaState);
		}
	}

	prefetch() {
		this._doStreamPrefetch();
	}

	stopFetching() {
		if(this.loaded && this._fetching) {
			this._fetching = false;
			this._hlsJsController.stopFetching();
		}
	}

	resumeFetching() {
		if(this._loaded && !this._fetching) {
			this._fetching = true;
			this._hlsJsController.resumeFetching(this.currentTime);
		}
	}

	pause() {
		this._doStreamPause();
	}

	play() {
		this._doStreamPlay();
	}

	playPause() {
		if (this._paused) {
			this.play();
		}
		else {
			this.pause();
		}
	}

	seek(seconds) {
		Utils.checkNotFiniteAndThrow(seconds, 'seek time');
		Utils.checkLessThanZeroPointZeroAndWarn(seconds, 'seek time'); //we fix this in _determineSeekTime
		logger.log( `seek: seconds: ${seconds}, currentTime: ${this.currentTime}, _seekableRange: [${this._seekableRange.start},${this._seekableRange.end}]`);

		if(this._playbackState === PlaybackState.NOTREADY || this._playbackState === PlaybackState.SEEKING || this._seekWaitingForBuffer) {
			logger.log( `seek: pendingSeek playbackState: ${this._playbackState} seekWaitingForBuffer: ${this._seekWaitingForBuffer}`);
			this._pendingSeek = seconds;
		}
		else {
			let targetTime = this._determineSeekTime(seconds);

			if((this._audioOnlySeekWorkaround && this.audioOnly) || this._dataReadySeekWorkaround) {
				let seekReady = false;

				for(let i = 0; i < this.bufferedRange.length; i++) {
					if(targetTime > this.bufferedRange[i].start && targetTime < this.bufferedRange[i].end) {
						seekReady = true;
					}
				}

				if(seekReady) {
					this._mediaController.seek(targetTime);
				}
				else {
					logger.log( `seek: _seekWaitingForBuffer`);
					this._hlsJsController.bufferBeforeSeeking = targetTime;
					this._seekWaitingForBuffer = true;
				}
			}
			else {
				this._mediaController.seek(targetTime);
			}
		}
	}

	seekByPdt(time) {
		if (!Utils.isValidDate(time)) {
			this._startTimeByPdt = time;
			throw new Error(`seekByPdt: ${time} is not a valid date`);
		}

		let pdtBaseDate = this._hlsJsController.basePdt;
		if (Utils.isValidDate(pdtBaseDate) && Utils.isValidDate(time)) {
			let seekTime = 0;

			//seekTime = (time.getTime() - pdtBaseDate.getTime()) / 1000;
			seekTime = this.getTimeForPdt(time);
			logger.log( `seekByPdt: seekTime: ${seekTime} time: ${time.getTime() / 1000} pdtBaseDate: ${pdtBaseDate.getTime() / 1000}`);
			this.seek(seekTime);
		}
	}

	seekToLive() {
		//TODO: need to apply some smarts if we haven't started yet, or are getting there
		if(this.live && this.currentTime < this.liveTime ) {
			logger.log( `seekToLive: liveTime: ${this.liveTime}, duration: ${this.duration}`);

			this.seek(this.liveTime);
		}
	}

	/**
	 * Calculate the correct seek time
	 *
	 * we don't want to seek before the start of the seekable range, and if live we don't want to seek after live time
	 *
	 * seeking after the end of the seekable range is fine, the media element already handles that
	 *
	 * @param {Number} requestTime - the requested seekTime in seconds.
	 */
	_determineSeekTime(requestTime) {
		let seekTime = requestTime;

		seekTime = Math.min(seekTime, this._seekableRange.end);

		if (this.live) {
			seekTime = Math.min(seekTime, this.liveTime);
		}

		seekTime = Math.max(seekTime, this._seekableRange.start);

		logger.log(`determineSeekTime: seeking to ${seekTime}`);

		return Number(seekTime.toFixed(6));
	}

	_resolvePendingSeek() {
		logger.log(`_resolvePendingSeek: _pendingSeek: ${this._pendingSeek} _seekableRange: [${this._seekableRange.start}, ${this._seekableRange.end}] currentTime: ${this.currentTime}`);

		if(this._pendingSeek !== this.currentTime) { //don't bother doing the pending seek if we're already at the target seek time
			this.seek(this._pendingSeek);
		}

		this._pendingSeek = undefined;
	}

	_onHlsJsSeekReady(data) {
		logger.log(`_onHlsJsSeekReady: time: ${data.time} _seekableRange: [${this._seekableRange.start}, ${this._seekableRange.end}] currentTime: ${this.currentTime}`);

		this._seekWaitingForBuffer = false;

		this._mediaController.seek(this._determineSeekTime(data.time));
	}

	_wireEvents() {
		this._handlers._wireHlsEvents( this, this.emitter );
	}

	_unwireEvents() {
		this._handlers._unwireHlsEvents( this.emitter );
	}

	_decidePlaybackState() {
		let newState;

		if (this._state !== State.PLAYBACK) {
			newState = PlaybackState.NOTREADY;
		}
		else if (this._ended) {
			newState = PlaybackState.ENDED;
		}
		else if (this._seeking) {
			newState = PlaybackState.SEEKING;
		}
		else if (this._paused) {
			newState = PlaybackState.PAUSED;
		}
		else if (this._buffering) {
			newState = PlaybackState.BUFFERING;
		}
		else {
			newState = PlaybackState.PLAYING;
		}

		if (newState === PlaybackState.NOTREADY && this._state === State.PLAYBACK) {
			throw new Error('PlaybackState should not be NOTREADY');
		}

		if (newState === undefined) {
			throw new Error('PlaybackState should not be undefined');
		}

		this._setPlaybackState(newState);
	}

	_setMediaState( newMediaState ) {
		if( !newMediaState ) {
			throw new Error( `invalid mediaState: ${newMediaState}`);
		}

		if (this._mediaState !== newMediaState) {
			let oldMediaState = this._mediaState;
			this._mediaState = newMediaState;

			this._doMediaStateChanged({ mediaState: newMediaState, lastMediaState: oldMediaState });
		}
	}

	_setPlaybackState( newPlaybackState ) {
		if( !newPlaybackState ) {
			throw new Error( `invalid playbackState: ${newPlaybackState}`);
		}

		if (this._playbackState !== newPlaybackState) {
			let oldPlaybackState = this._playbackState;
			this._playbackState = newPlaybackState;

			this._doStreamPlaybackStateChanged({
				playbackState: newPlaybackState,
				lastPlaybackState: oldPlaybackState,
			});
		}
	}

	_setState(newState) {
		if( !newState ) {
			throw new Error( `invalid state: ${newState}`);
		}

		if (this._state !== newState) {
			let oldState = this._state;
			this._state = newState;

			this._doStreamStateChanged({ state: newState, lastState: oldState });
		}
	}

	_resetState() {
		if (!this._disposed) {
			logger.log('HlsController::_resetState');
			this._paused = true;
			this._canplay = false;
			this._canplaythrough = false;
			this._ended = false;
			this._seeking = false;
			this._buffering = false;
			this._loaded = false;
			this._fetching = false;
			this._prefetch = false;
			this._startPositionSet = false;
			this._canplayWaiting = false;
			this._markers.clearAllMarkers();
			this._seekableRange.start = undefined;
			this._seekableRange.end = undefined;
			this._seekableRange.duration = undefined;
			this._playbackRange.start = undefined;
			this._playbackRange.end = undefined;
			this._playbackRange.duration = undefined;
			this._prevDuration = 0;
			this._canplayWorkaroundCount = 0;
		}
	}

	/**
	 * this is purely used to set the internal media, and thus post when the result of
	 * get media has changed.
	 */
	_setMedia(newMedia) {
		logger.log('HlsController::_setMedia');

		if( newMedia === undefined ) {
			throw new Error( `invalid media: ${newMedia}`);
		}

		if (this._media !== newMedia) {
			let oldMedia = this._media;
			this._media = newMedia;

			let data = { media: newMedia, lastMedia: oldMedia };
			logger.log(`STREAM_MEDIA_CHANGED: { media: ${data.media} lastMedia: ${data.lastMedia} }`);

			this._emit(Event.STREAM_MEDIA_CHANGED, data);
		}
	}

	_startFetching() {
		// se: ran into an issue where during disposal, this blew up... hacked in the penddispose check
		// but now its blowing up again during "prefetch then play works"
		// need to work out why State is not LOADED.
		if( !this._fetching) {
			if (this._state !== State.LOADED ) {
				throw new Error(`can't start fetching if not loaded! state: ${this._state}`);
			}

			this._beginStreamFetching();
		}
	}

	_doStreamStateChanged(data) {
		logger.log(`STREAM_STATE_CHANGED: { state: ${data.state} lastState: ${data.lastState} }`);

		if (data.state === State.PLAYBACK || data.lastState === State.PLAYBACK) {
			this._decidePlaybackState();
		}
		this._emit(Event.STREAM_STATE_CHANGED, data);
	}

	/**
	 * log, emit and process the STREAM_PLAYBACKSTATE_CHANGED event.
	 *
	 * determines when buffering/seeking have finished
	 *
	 * dispatches to the next playback state handler based on the new state.
	 *
	 * @emits STREAM_PLAYBACKSTATE_CHANGED
	 * @emits STREAM_BUFFERING_FINISHED
	 * @emits STREAM_SEEKING_FINISHED
	 *
	 * @param {Object} data - event data
	 * @param {PlaybackState} data.playbackState - the new playbackState
	 * @param {PlaybackState} data.lastPlaybackState - the last playbackState
	 */
	_doStreamPlaybackStateChanged(data) {
		logger.log(`STREAM_PLAYBACKSTATE_CHANGED: { playbackState: ${data.playbackState} lastPlaybackState: ${data.lastPlaybackState} }`);

		if(data.playbackState !== PlaybackState.NOTREADY && data.playbackState !== PlaybackState.SEEKING
		&& this._pendingSeek !== undefined) {
			//seek was requested while we were unable to do anything with it, now that we can, resolve it
			this._resolvePendingSeek();
		}

		if (data.playbackState !== this._playbackState) {
			throw new Error(`playbackState: ${this._playbackState} out of sync with new playbackState ${data.playbackState}`);
		}

		switch (data.playbackState) {
		case PlaybackState.PAUSED:
			this._doPlaybackPaused();
			break;
		case PlaybackState.BUFFERING:
			this._doPlaybackBuffering();
			break;
		case PlaybackState.PLAYING:
			this._doPlaybackPlaying();
			break;
		case PlaybackState.SEEKING:
			this._doPlaybackSeeking();
			break;
		case PlaybackState.ENDED:
			this._doPlaybackEnded();
			break;
		case PlaybackState.NOTREADY:
			//this._onPlaybackNotReady();
			this._emit(Event.PLAYBACK_NOTREADY);
			break;

		default:
			throw new Error('invalid playback state transition');
		}

		this._emit(Event.STREAM_PLAYBACKSTATE_CHANGED, data);

		switch (data.lastPlaybackState) {
		case PlaybackState.BUFFERING:
			logger.log('STREAM_BUFFERING_FINISHED');
			this._emit(Event.STREAM_BUFFERING_FINISHED);
			break;
		case PlaybackState.SEEKING:
			logger.log('STREAM_SEEKING_FINISHED');
			this._emit(Event.STREAM_SEEKING_FINISHED);
			break;
		}
	}

	/**
	 * log and emit the STREAM_MEDIASTATE_CHANGED event
	 */
	_doMediaStateChanged(data) {
		logger.log(`STREAM_MEDIASTATE_CHANGED: { mediaState: ${data.mediaState} lastMediaState: ${data.lastMediaState} }`);

		this._emit(Event.STREAM_MEDIASTATE_CHANGED, data);
	}

	/**
	 *  fired by HlsJsController
	 */
	_onManifestParsed(data) {
		if(this._recoveryOptions.startPdt !== undefined) {
			this._hlsJsController.recoveryStartTimeByPdt = this._recoveryOptions.startPdt;
			this._recoveryOptions.startPdt = undefined;
		}
		this._doStreamLoaded(data);

		// this kicks us along the prefetch/autoprefetch/
		this._afterStreamLoaded();
	}

	/**
	 *  used when we have an error, or when the user changes source.
	 */
	_unloadSource() {
		this._onStreamUnloaded();
		this._emit(Event.STREAM_UNLOADED);
	}

	/**
	 * in response to MEDIA_CONTROLLER_ERROR fired by MediaController.
	 */
	_handleMediaControllerError(data) {

		let theError = {};

		theError._data = data;
		theError.fatal = true;
		theError.cause = data.details;
		theError.streamId = this._instanceId;
		theError.source = this._source;
		theError.lastState = this._state;
		theError.lastPlaybackState = this._playbackState;
		theError.lastMediaState = this._mediaState;

		if(data.errorObj !== undefined ) {
			theError.errorObj = data.errorObj;
		}

		theError.error = HlsError.MEDIA_CONTROLLER_FAILED;
		theError.message = HlsErrorString[theError.error];
		theError.messageDetailed = `[${theError.error}] ${theError.message} (${theError.cause})`;
		theError.willDetachMedia = true;
		theError.willUnload = false;

		if (theError.willDetachMedia) {
			logger.log('MediaController Error; Detaching due to error');
			this._safeDoNextTick( () => this._emit(Event.MEDIA_DETACHING, { media: null } ));
		}

		this._emit(Event.STREAM_ERROR, theError);
	}

	_emitError( errorFatal, errorCause, errorObj ) {

		let errorData = {};

		errorData.details = errorCause;
		errorData.err = errorObj;
		errorData.fatal = errorFatal;

		this._handleError(errorData);
	}

	_emitWarning( errorCause, errorObj ) {
		this._emitError( false, errorCause, errorObj );
	}
	/**
	 * in response to MEDIA_ELEMENT_ERROR fired by MediaController.
	 */
	_handleMediaElementError(data) {
		logger.warn('media element errored out');

		let theError = {};

		theError._data = data;
		theError.cause = data.details;
		theError.streamId = this._instanceId;
		theError.source = this._source;
		theError.lastState = this._state;
		theError.lastPlaybackState = this._playbackState;
		theError.lastMediaState = this._mediaState;

		if(data.errorObj !== undefined ) {
			theError.errorObj = data.errorObj;
		}

		if(data.mediaErrorObj !== undefined) {
			theError.mediaErrorObj = data.mediaErrorObj;
		}

		theError.error = HlsError.MEDIA_ELEMENT_FAILED;
		theError.message = HlsErrorString[theError.error];
		theError.messageDetailed = `[${theError.error}] ${theError.message} (${theError.cause})`;

		if(this._autoRecover) {
			logger.log('media error recovery: autoRecover === true');
			theError.willDetachMedia = false;
			theError.willUnload = false;
			theError.fatal = false;

			this._emit(Event.STREAM_ERROR, theError);

			if(this._media) {
				logger.log('Attempting to recover media element');
				//HlsJs's auto recover doesn't work for us as we need to reset a lot of things outside of HlsJs
				this._doMediaErrorRecovery(true, true);
			}
		}
		else {
			logger.log('media error recovery: autoRecover === false, treating as fatal');
			theError.willDetachMedia = true;
			theError.willUnload = false;
			theError.fatal = true;

			this._emit(Event.STREAM_ERROR, theError);

			logger.log('MediaElement Error; Detaching due to error');
			this._doDetach();
		}
	}

	_handleError(data) {
		let theError = {};
		let shouldUnload = false;

		theError._data = data;
		theError.fatal = data.fatal;
		theError.cause = data.details;
		theError.streamId = this._instanceId;
		theError.source = this._source;
		theError.lastState = this._state;
		theError.lastPlaybackState = this._playbackState;
		theError.lastMediaState = this._mediaState;

		if(data.response !== undefined ) {
			theError.response = data.response;
		}

		if(data.err !== undefined ) {
			theError.errorObj = data.err;
		}

		// TODO: we should consider returning the currentTime (lastTime?) to
		// allow retrying with startTime = lastTime

		if( theError.cause === HlsJsError.INTERNAL_EXCEPTION ) {
			// we shouldn't get internalExceptions...
			theError.fatal = true;
		}

		// when click-to-play browsers are used, media.play can fail.
		// we then pause. Sometimes if you set isMuted=true, and try again
		// it might play.
		if( theError.cause === HlsJsError.MEDIA_PLAY_FAILED ) {
			this.pause();
		}

		if( theError.cause === HlsJsError.BUFFER_STALLED_ERROR ) {
			this._buffering = true;
			this._decidePlaybackState();

			this._emit(Event.STREAM_BUFFERING_STARTED);

			// swallow the error
			return;
		}
		if( theError.cause === HlsJsError.BUFFER_SEEK_OVER_HOLE && this._seekOverHoleWorkaround ) {
			// swallow unnecessary PLAYBACK_SEEKING events in IE
			this._swallowNextSeek = true;
		}

		// calculate error code
		if( !theError.fatal ) {
			// a warning may eventually trigger a fatal error, but not this time.
			theError.error = HlsError.WARNING;
		}
		else {
			switch( this._state ) {
			case State.UNLOADED:
				theError.error = HlsError.UNEXPECTED_ERROR;
				throw new Error(`Should be impossible to have an error in state: ${this._state}, data:${data}`);
				//break;

			case State.LOADING:
				theError.error = HlsError.LOAD_FAILED;
				shouldUnload = true;
				break;

			case State.FETCHING:
				theError.error = HlsError.FETCH_FAILED;
				shouldUnload = true;
				break;

			case State.PLAYBACK:
				theError.error = HlsError.PLAYBACK_FAILED;
				shouldUnload = true;
				break;

			default:
				theError.error = HlsError.UNEXPECTED_ERROR;
				shouldUnload = true;
				throw new Error(`Should be impossible to be in this state : ${this._state}`);
				//break;
			}
		}

		// add the error message
		theError.message = HlsErrorString[theError.error];
		theError.messageDetailed = `[${theError.error}] ${theError.message} (${theError.cause})`;

		theError.willUnload = shouldUnload;
		theError.willDetachMedia = false; // FIXME: this needs to be derived.

		this._emit(Event.STREAM_ERROR, theError);

		if (shouldUnload) {
			logger.log('Unloading due to error');
			this._mediaController.pause();
			this.stopFetching();
			this._unloadSource();
		}
	}

	// also fired by HlsJsController
	_onHlsJsError(data) {
		/* TODO: this needs a lot more work! We may even want an ErrorController */
		logger.log('STREAM_ERROR');
		logger.log(data);

		this._handleError(data);
	}


	_doMediaErrorRecovery(force, recoverTime) {
		if (this._mediaErrorRecovery || force) {

			logger.log('_doMediaErrorRecovery: force: ' + force + ' _mediaErrorRecovery: ' + this._mediaErrorRecovery);

			this._mediaErrorRecovery = true;

			logger.log('_doMediaErrorRecovery: starting recovery');

			if (this._hlsJsController.basePdt) {
				this._hlsJsController._resumeBasePdt = this._hlsJsController.basePdt;
			}
			logger.log(`_doMediaErrorRecovery: _resumeBasePdt ${this._hlsJsController._resumeBasePdt}`);

			if(!this._paused) {
				this._playOnMediaErrorRecovery = true;
				logger.log('media error recovery: playback state not paused, will autoplay after recovery');
			}
			else {
				logger.log('media error recovery: playback state paused, will not autoplay');
			}

			if (recoverTime) {
				this._recoveryOptions.startPdt = this.getPdtForTime(this.currentTime);
				logger.log(`media error recovery: recovered currentTime: ${this._recoveryOptions.startPdt}`);
			}

			else if(this.live && this.liveTime !== 0) {
				this._recoveryOptions.startPdt = this.getPdtForTime(this.liveTime);
				logger.log(`media error recovery: recovered liveTime: ${this._recoveryOptions.startPdt}`);
			}
			else {
				this._recoveryOptions.startPdt = this.getPdtForTime(0);
				logger.log(`media error recovery: zeroed currentTime: ${this._recoveryOptions.startPdt}`);
			}

			this._recoveryOptions.playbackRate = this.playbackRate;
			logger.log(`media error recovery: recovered playbackRate: ${this._recoveryOptions.playbackRate}`);
			this._recoveryOptions.volume = this.volume;
			logger.log(`media error recovery: recovered volume: ${this._recoveryOptions.volume}`);

			if (this.currentAudioRendition) {
				this._hlsJsController.recoveryAudioIndex = this.currentAudioRendition;
				logger.log(`media error recovery: recovered and applied audio rendition: ${this.currentAudioRendition}`);
			}

			this._pendingUrl = this._source;
			this._pendingMedia = this._media;
			this._doDetach();
		}
		else {
			logger.log('_doMediaErrorRecovery: already in recovery');
		}
	}


	_onHlsJsMediaAttached(data) {
		logger.log(`HLSJS_MEDIA_ATTACHED: { media: ${data.media} mediaState: ${this._mediaState} }`);

		if( this._mediaState === MediaState.ATTACHING ) {
			this._emit( Event.MEDIA_ATTACHED, data );
		}
		else if ( this._mediaState === MediaState.DETACHING ) {
			this._hlsJsController.detachMedia();
		}
		else {
			throw new Error( `HlsController::_onHlsJsMediaAttached invalid mediaState { ${this._mediaState} }`);
		}
	}

	_onHlsJsMediaDetached(data) {
		logger.log(`HLSJS_MEDIA_DETACHED: { media: ${data.media} mediaState: ${this._mediaState} }`);

		if ( this._mediaState === MediaState.DETACHING ) {
			// will trigger MEDIA_CONTROLLER_UNWIRED
			this._mediaController.media = null;
		}
		else {
			throw new Error( `HlsController::_onHlsJsMediaDetached invalid mediaState { ${this._mediaState} }`);
		}
	}

	_onChunkBuffered() {
		this._updatePlaybackRange();

		if(this._canplayWorkaround && !this._canplay) {
			//Firefox is very cautious about declaring canplay/canplaythrough compared to other browsers that do it as
			//soon as possible. In some cases Firefox doesn't send canplay at all.
			//This workaround causes canplay to be triggered at around the same time as other browsers, greatly
			//increasing performance.
			this._canplayWorkaroundCount++;

			if(this._canplayWorkaroundCount > 2) {
				//forcing canplay on the first two frag loads interferes with startTimeByPdt
				this._doStreamCanPlay();
			}
		}

		if(this._checkIfInBufferedRange()) {
			this._buffering = false;
			if(this._state === State.PLAYBACK && this._seeking && !this._disableFakeSeek) {
				//If we're in this situation, a seek has happened, and resolved, but MEDIA_SEEKED has not fired
				//run mediaController._onMediaSeeked here to properly reset the state of the controllers
				logger.log(`faking a MEDIA_SEEKED`);
				this._mediaController._onMediaSeeked();
				//this._emit(Event.MEDIA_SEEKED, { currentTime : this.currentTime });
			}
		}
		this._decidePlaybackState();

		this._markers.trimMeta(this.bufferedRange[0].start);
	}

	_onHlsJsSubtitleUpdate() {
		this._mediaController.captionsControl();
	}

	_onVariantDurationUpdate(data) {
		if(this._seekableRange.start === undefined ||
		this._seekableRange.start < (data.totalDuration - data.playlistDuration) ||
		this._seekableRange.end < data.totalDuration) {
			this._seekableRange.end = data.totalDuration;
			this._seekableRange.duration = data.playlistDuration;
			this._seekableRange.start = Math.max((this._seekableRange.end - this._seekableRange.duration), 0);
		}

		this._checkIfInSeekableRange();
		this._updatePlaybackRange();
	}

	_onStartPositionSet(data) {
		this._startPositionSet = true;
		this._startPosition = data.startPosition;

		if(this._canplayWaiting) {
			this._doStreamCanPlay();
		}
	}

	// Player States
	_onStreamUnloaded() {
		logger.log('STREAM_UNLOADED');
		this._setState(State.UNLOADED);

		// need to reset stuff.
		this._prefetch = false;
	}

	_beginStreamLoading(url) {
		logger.log( `STREAM_LOADING: { url: ${url} }`);

		this._source = url;

		this._setState(State.LOADING);
		this._hlsJsController.loadSource(url);

		// if we have a source, and media, we should attach.
		// this is because we have a requirement to NOT attach when
		// source is null
		// If the source wasn't attached when WIRED came through, we'll
		// be stuck in WIRED waiting for a source.
		if( this._source !== null && this._mediaState === MediaState.WIRED ) {
			logger.log( `_beginStreamLoading: mediaState: ${this._mediaState}`);

			this._attemptMediaAttaching();
		}

		this._emit(Event.STREAM_LOADING, { url: url } );
	}

	// after we've done _doStreamLoaded AND emitted the event
	_afterStreamLoaded() {
		if (!this._prefetch && (this._autoPrefetch || this._paused === false) ) {
			this.prefetch();
		}
		if (this._mediaErrorRecovery) {
			logger.log('media error recovery: reapplying state before error');
			if (this._playOnMediaErrorRecovery) {
				this.play();
				this._playOnMediaErrorRecovery = false;
				logger.log('media error recovery: playing video');
			}

			if(this._recoveryOptions.playbackRate) {
				this.playbackRate = this._recoveryOptions.playbackRate;
				this._recoveryOptions.playbackRate = undefined;
				logger.log(`media error recovery: reapplying playbackRate: ${this.playbackRate}`);
			}

			if(this._recoveryOptions.volume) {
				this.volume = this._recoveryOptions.volume;
				this._recoveryOptions.volume = undefined;
				logger.log(`media error recovery: reapplying volume: ${this.volume}`);
			}

			this._mediaErrorRecovery = false;
		}
		else if (this.paused && this._autoPlay) {
			this.play();	// will take care of prefetch for the autoplay case.
		}

		// we'll issues the play to the media after CANPLAYTHROUGH.
	}

	_doStreamLoaded(data) {
		logger.log(`STREAM_LOADED`);

		this._loaded = true;

		this._setState(State.LOADED);

		this._emit(Event.STREAM_LOADED, data);
	}

	_beginStreamFetching() {
		logger.log('STREAM_FETCHING');

		this._fetching = true;
		this._setState(State.FETCHING);

		this._hlsJsController.startFetching();

		this._emit(Event.STREAM_FETCHING);
	}

	_beginStreamPlayback() {
		logger.log('STREAM_PLAYBACK');
		this._setState(State.PLAYBACK);	// will decide playbackState

		this._emit(Event.STREAM_PLAYBACK);

	}

	// PLAYBACK STATES
	_doPlaybackSeeking() {
		if(this._swallowNextSeek) {
			this._swallowNextSeek = false;
		}
		else {
			logger.log('PLAYBACK_SEEKING');
			this._emit(Event.PLAYBACK_SEEKING);
		}
	}

	_doPlaybackPaused() {
		logger.log('PLAYBACK_PAUSED');
		this._mediaController.pause();
		this._emit(Event.PLAYBACK_PAUSED);
	}

	_doPlaybackBuffering() {
		logger.log('PLAYBACK_BUFFERING');
		this._emit(Event.PLAYBACK_BUFFERING);
	}

	_doPlaybackPlaying() {
		logger.log('PLAYBACK_PLAYING');
		let playPromise = this._mediaController.play();
		this._emit(Event.PLAYBACK_PLAYING);

		playPromise.then(() => {
			if(this._seekBackOnPlay) {
				this.currentTime -= 0.000001;
				this._seekBackOnPlay = false;
			}
		}).catch((err) => {
			if (!this._disposed && !this._paused ) {
				this._emitWarning( HlsJsError.MEDIA_PLAY_FAILED, err );
			}
		});
	}

	_doPlaybackEnded() {
		logger.log('PLAYBACK_ENDED');
		// no need to pause mediaController. If we seek etc, it'll use the last state.
		this.playbackRate = 1;
		this._emit(Event.PLAYBACK_ENDED);
	}

	/*
		we seem to get Media CANPLAY just after buffer depletions...
	*/
	_doStreamCanPlay() {
		logger.log('STREAM_CANPLAY');

		if(this._lateStartWorkaround && this.audioOnly) {
			this.seek(this._startPosition);
		}

		this._canplay = true;
		this._initialTime = this._startPosition;

		this._buffering = true;

		if (this._state !== State.FETCHING) {
			throw new Error('impossible not to be in fetching when canplay comes through');
		}

		this._emit(Event.STREAM_CANPLAY);

		// decide playback happens onStreamPlayback
		this._beginStreamPlayback();

	}

	// and media CANPLAYTHROUGH seems to signal end of media buffering
	_doStreamCanPlayThrough() {
		logger.log('STREAM_CANPLAYTHROUGH');

		this._canplaythrough = true;

		this._buffering = false;
		this._ended = false;

		// running a captions control here fixes a couple of issues that are detailed in the media controller
		this._mediaController.captionsControl();

		this._emit(Event.STREAM_CANPLAYTHROUGH);

		this._decidePlaybackState();
	}

	// commands
	_doStreamPrefetch() {
		if (this._state === State.LOADED) {
			this._prefetch = true;
			this._emit(Event.STREAM_PREFETCH);
			this._startFetching();
		}
	}


	// perhaps need a pause/play/buffer/fetching decider function.
	_doStreamPause() {
		logger.log('HlsController::_doStreamPause');
		this._paused = true;
		this._emit(Event.STREAM_PAUSE);

		this._decidePlaybackState();
	}

	_doStreamPlay() {
		logger.log('HlsController::_doStreamPlay');
		this._paused = false;
		this._ended = false;
		if(this._checkIfInBufferedRange()) {
			this._buffering = false;
		}

		this._emit(Event.STREAM_PLAY);

		// if we aren't already, we need prefetch()
		if (!this._prefetch) {
			this.prefetch();
		}
		this._decidePlaybackState();
	}

	_attemptMediaAttaching() {
		logger.log('HlsController::_attemptMediaAttaching');

		if( this._mediaState === MediaState.WIRED && this._source !== null ) {
			this._emit(Event.MEDIA_ATTACHING, { media: this._media } );
		}
	}

	_attachPendingMediaOrUrl() {
		logger.log(`HlsController::_attachPendingMediaOrUrl { mediaState: ${this._mediaState} }`);

		if (this._mediaState !== MediaState.DETACHING ) {
			logger.log('HlsController::_attachPendingMediaOrUrl checking for pending Url or Media');
			if( this._pendingMedia ) {
				if( this._media ) {
					throw new Error('_attachPendingMediaOrUrl with valid media already set!');
				}

				logger.log(`HlsController::_attachPendingMediaOrUrl: wiring media ${this._pendingMedia}`);

				this._setMedia(this._pendingMedia);
				this._pendingMedia = null;

				this._emit( Event.MEDIA_WIRING, { media: this._media } );

//console.log(`preBUMP _prefetch: ${this._prefetch} _paused: ${this._paused}
// _autoPrefetch: ${this._canplay} _canplay: ${this._canplay} _state: ${this._state}`);
				//This is causing regressions in tests that load source before attaching media. Need a better solution.
				/*if (!this._canplay && this._source !== null && this._state === State.FETCHING) {
					logger.log('Likely load stall. Reload.'); //Not the best solution. Investigate. - SIEG
					this._pendingUrl = this._source;
				}*/

			}
			if( this._pendingUrl ) {

				logger.log(`HlsController::_attachPendingMediaOrUrl: setting url ${this._pendingUrl}`);
				this.loadPendingSource();
			}
		}
	}

	_onMediaWiring( data ) {
		// no nulll, no undefined.
		if( !data.media ) {
			throw new Error( `HlsController::_onMediaWiring, invalid media { media: ${data.media} }`);
		}

		logger.log(`MEDIA_WIRING: { media: ${data.media} mediaState: ${this._mediaState} }`);

		this._setMediaState(MediaState.WIRING);

		this._mediaController.media = data.media;
	}

	_onMediaWired( data) {
		logger.log(`MEDIA_WIRED: { media: ${data.media} mediaState: ${this._mediaState} }`);

		this._media = data.media;

		this._setMediaState(MediaState.WIRED);

		// if we have a pending detach, might as well skip attaching and fake a MEDIA_DETACHED

		if( this._source !== null ) {
			logger.log(`_onMediaWired, queuing _attemptMediaAttaching`);

			// to keep things ordered, we'll delay attaching until nextTick.
			this._safeDoNextTick( () => this._attemptMediaAttaching() );
		}

	}

	_onMediaAttaching( data ) {
		logger.log(`MEDIA_ATTACHING: { media: ${data.media} mediaState: ${this._mediaState} }`);

		if( !(data.media instanceof HTMLMediaElement) ) {
			throw new Error( `HlsController::_onMediaAttaching, invalid media { media: ${data.media} }`);
		}

		if( this._mediaState !== MediaState.WIRED ) {
			throw new Error( `HlsController::_onMediaAttaching, invalid state { mediaState: ${this._mediaState} }`);
		}

		this._setMediaState( MediaState.ATTACHING );

		logger.log(`_onMediaAttaching, queuing _hlsJsController.attachMedia`);

		// attachMedia will emit HLSJS_MEDIA_ATTACHED eventually
		this._safeDoNextTick( () => this._hlsJsController.attachMedia( data.media ) );

	}

	_onMediaAttached(data) {
		logger.log(`MEDIA_ATTACHED: { media: ${data.media}, mediaState: ${this._mediaState} }`);

		if( this._mediaState !== MediaState.ATTACHING ) {
			throw new Error( `HlsController::_onMediaAttached invalid state { mediaState: ${this._mediaState} }`);
		}
		// otherwise we're good to go to attached
		this._setMediaState(MediaState.ATTACHED);

	}

	_onMediaDetaching( data ) {
		logger.log(`MEDIA_DETACHING: { media: ${data.media} mediaState: ${this._mediaState} }`);

		if( this._mediaState === MediaState.DETACHED ) {
			throw new Error( `HlsController::_onMediaDetaching invalid mediaState { ${this._mediaState }}`);
		}

		let lastMediaState = this._mediaState;

		this._setMediaState( MediaState.DETACHING );

		// if media is ATTACHING or WIRING, it will resolve DETACHING when it completes

		// if we're wiring or wired, then set media to null.
		if (lastMediaState === MediaState.WIRING || lastMediaState === MediaState.WIRED) {
			logger.log(`HlsController::_onMediaDetaching unwiring mediaController`);

			this._safeDoNextTick( () => {
				if( this._mediaController.media ) {
					this._mediaController.media = null;
				}
				else {
					/* if mediaController is already nulled, then it won't emit UNWIRED */
					this._emit( Event.MEDIA_CONTROLLER_UNWIRED, { media: null } );
				}

			});
		}
		else if (lastMediaState === MediaState.ATTACHED ) {
			logger.log(`HlsController::_onMediaDetaching detaching hlsJsController`);
			this._hlsJsController.detachMedia();
		}

		// all other cases nothing to do.
	}

	_onMediaDetached(data) {
		logger.log(`MEDIA_DETACHED: { media: ${data.media} }`);

		this._setMedia(null);

		this._setMediaState(MediaState.DETACHED);
		this._resetState();

		if (this._pendingDispose) {
			logger.log(`HlsController::_onMediaDetached processing _pendingDispose`);
			//this._doDispose();

			// we need to do the dispose after the next tick to ensure all listeners can handle
			// the MEDIA_DETACHED event before the final STREAM_DISPOSED event is emitted.
			process.nextTick( () => this._doDispose() );

		}
		else if (!this._disposed) {
			// if we have a pending media or url... its time to handle it.
			this._attachPendingMediaOrUrl();
		}
	}


	_safeDoNextTick( lambda ) {
		process.nextTick( () => {
			if( !this._disposed ) {
				lambda();
			}
		});
	}

	_onMediaControllerWired( data) {
		logger.log(`MEDIA_CONTROLLER_WIRED: { media: ${data.media} mediaState: ${this._mediaState} }`);

		if( this._mediaState === MediaState.WIRING ) {
			this._safeDoNextTick( () => this._emit(Event.MEDIA_WIRED, data ) );
		}
		else if( this._mediaState === MediaState.DETACHING ) {
			this._mediaController = null;
		}
		else {
			throw new Error( `HlsController::_onMediaControllerWired invalid state { mediaState: ${this._mediaState} }`);
		}
	}

	_onMediaControllerUnwired( data ) {
		logger.log(`MEDIA_CONTROLLER_UNWIRED: { media: ${data.media} mediaState: ${this._mediaState} }`);

		if( this._mediaState === MediaState.DETACHING ) {
			this._safeDoNextTick( () => this._emit( Event.MEDIA_DETACHED, data ) );
		}
		else {
			throw new Error( `HlsController:_onMediaControllerUnwired invalid state { mediaState: ${this._mediaState} }`);
		}
	}

	_onMediaControllerError(data) {
		logger.log(`MEDIA_CONTROLLER_ERROR: { data: ${data} }`);

		this._handleMediaControllerError(data);
	}

	_onMediaElementError(data) {
		logger.log(`MEDIA_ELEMENT_ERROR: { data: ${data} }`);

		this._handleMediaElementError(data);
	}

	_onMediaCanPlayThrough() {
		logger.log('MEDIA_CANPLAYTHROUGH');

		if (!this._canplaythrough) {
			this._doStreamCanPlayThrough();
		}
		else {
			logger.log('redundant CANPLAYTHROUGH call, ignoring');
		}

	}

	_onMediaDurationChange() {
		logger.log('MEDIA_DURATIONCHANGE');

		let data = { duration:this._mediaController.duration, lastDuration:this._prevDuration };

		this._prevDuration = this._mediaController.duration;

		this._emit(Event.STREAM_DURATION_CHANGED, data);
	}

	_onMediaTimeUpdate( data ) {
		this._updatePlaybackRange();

		if(this.live) {
			if(this.playbackRate > 1 && this.currentTime > this.liveTime) {
				logger.log('live time reached, setting playback rate to 1');
				this.playbackRate = 1;
			}
			else if(this.currentTime <= this.seekableRange.start && this.playbackRate < 1) {
				logger.log('start of stream reached, setting playback rate to 1');
				this.playbackRate = 1;
			}
		}
		this._emit(Event.STREAM_TIMEUPDATE, data );
	}

	_onMediaCanPlay() {
		logger.log('MEDIA_CANPLAY');

		if (!this._canplay) {
			if(this._startPositionSet) {
				this._doStreamCanPlay();
			}
			else {
				this._canplayWaiting = true;
			}
		}
		else {
			logger.log('redundant CANPLAY call, ignoring');
		}
	}

	_onMediaSeeking() {
		if (this._mediaState !== MediaState.DETACHING) { //probably a safe-shutdown seek.
			logger.log('MEDIA_SEEKING');
			if(!this._fetching && this._state === State.PLAYBACK) {
				//stopFetching() was called at some point and we need to resume fetching or the seek will never finish
				this.resumeFetching();
			}

			this._seeking = true;
			this._ended = false;
			logger.log( `playbackRange: [${this._playbackRange.start},${this._playbackRange.end},${this._playbackRange.duration}], isLive: ${this.live}`);
		}
		this._decidePlaybackState();
	}

	_onMediaSeeked() {
		if (this._seeking) {
			logger.log('MEDIA_SEEKED');
			this._seeking = false;
		}
		if(this._checkIfInBufferedRange()) {
			this._buffering = false;
		}

		this._decidePlaybackState();
	}

	_onMediaEnded() {
		//console.log('MEDIA_ENDED');
		this._ended = true;
		this._seeking = false;
		this._buffering = false;
		//this._canplay = false;
		//this._canplaythrough = false;

		this._decidePlaybackState();
	}

	_onMediaRateChange() {
		logger.log('MEDIA_RATECHANGE');

		let lastPlaybackRate = this._lastPlaybackRate;
		this._lastPlaybackRate = this.playbackRate; //need to update this before the event is sent in case the rate is changed as a response to the event

		this._emit(Event.STREAM_PLAYBACKRATE_CHANGED, { playbackRate: this.playbackRate, lastPlaybackRate: lastPlaybackRate });

		if(this._forcePlaybackRateWorkaround || (this._resyncPlaybackRateWorkaround && this.playbackRate === 1 && lastPlaybackRate !== 1)) {
			if(this._paused) {
				this._seekBackOnPlay = true;
			}
			else {
				this.currentTime -= 0.000001;
			}
		}
	}

	_updatePlaybackRange() {
		this._playbackRange.start = this.currentTime;
		this._playbackRange.end = Math.min(this.seekableRange.end, this.bufferedRangeCurrent.end);
		this._playbackRange.duration = this._playbackRange.end - this._playbackRange.start;
	}

	_checkIfInSeekableRange() {
		logger.log(`_checkIfInSeekableRange: currentTime: ${this.currentTime} _seekableRange.start: ${this._seekableRange.start}`);

		if(this._seekableRange.start > this.currentTime) {
			let data = { currentTime: this.currentTime, windowStart: this._seekableRange.start,
				timeBehindWindow: this._seekableRange.start - this.currentTime };
			logger.warn(`playhead has fallen behind sliding window start @${this.currentTime}`);
			//if we're not buffered, or we're not live, readjust to the start of the range
			//we need to bypass seek here as we may be in NOTREADY or SEEKING when this happens and don't want to buffer a pending seek
			if(!this._checkIfInBufferedRange() || !this.live) {
				//we're going a millisecond in as in some cases, skipping to the very start of the range just doesn't work
				logger.warn(`attempting to correct playhead`);
				this._mediaController.currentTime = this.seekableRange.start + 0.001;
			}

			this._emit(Event.STREAM_PLAYHEAD_BEHIND_SLIDING_WINDOW, data);
		}
	}

	_checkIfInBufferedRange() {
		for(let i = 0; i < this.bufferedRange.length; i++) {
			if(this.bufferedRange[i].start !== this.bufferedRange[i].end &&
				this.currentTime >= this.bufferedRange[i].start &&
				this.currentTime <= this.bufferedRange[i].end) {
				return true;
			}
		}

		return false;
	}

	get emitter() {
		return this._emitter;
	}

	on(event, func)	{
		Utils.throwIfNotEvent(event);

		this._emitter.on(event, func);
	}

	off(event, func)	{
		Utils.throwIfNotEvent(event);

		this._emitter.off(event, func);
	}

	once(event, func)	{
		Utils.throwIfNotEvent(event);

		this._emitter.once(event, func);
	}

	_emit(event, ...args)	{
		this._emitter.emit(event, ...args);
	}

	get isMediaAttached() {
		return this._mediaState === MediaState.ATTACHED;
	}

	get state() {
		return this._state;
	}

	get playbackState() {
		return this._playbackState;
	}

	get mediaState() {
		return this._mediaState;
	}

	get src() {
		let retSource = null;

		if (this._pendingUrl) {
			retSource = this._pendingUrl;
		}
		else {
			retSource = this._source;
		}
		return retSource;
	}

	set src(url) {
		this.loadSource(url);
	}

	get media() {
		return this._media;
	}

	set media(video) {
		this.attachMedia(video);
	}

	get autoPrefetch() {
		return this._autoPrefetch;
	}

	set autoPrefetch(value) {
		this._autoPrefetch = !!value;
	}

	get autoPlay() {
		return this._autoPlay;
	}

	set autoPlay(value) {
		this._autoPlay = !!value;
	}

	get autoRecover() {
		return this._autoRecover;
	}

	set autoRecover(value) {
		this._autoRecover = !!value;
	}

	get nudgeOnStall() {
		return this._hlsJsController.nudgeOnStall;
	}

	set nudgeOnStall(value) {
		this._hlsJsController.nudgeOnStall = value;
	}

	/**
	 * if true, and the stream is live, the stream will start at the liveTime
	 *
	 * @type {boolean}
	 */
	get liveStreamsStartAtLiveTime() {
		return this._hlsJsController.liveStreamsStartAtLiveTime;
	}

	set liveStreamsStartAtLiveTime(value) {
		this._hlsJsController.liveStreamsStartAtLiveTime = value;
	}

	get xhrSetup() {
		return this._hlsJsController.xhrSetup;
	}

	set xhrSetup(func) {
		this._hlsJsController.xhrSetup = func;
	}

	get keyLoadCallback() {
		return this._hlsJsController.keyLoadCallback;
	}

	set keyLoadCallback(func) {
		this._hlsJsController.keyLoadCallback = func;
	}

	get obtainKeySystemConfigCallback() {
		return this._hlsJsController.obtainKeySystemConfigCallback;
	}

	set obtainKeySystemConfigCallback(func) {
		this._hlsJsController.obtainKeySystemConfigCallback = func;
	}

	// stage booleans.
	get loaded() {
		return this._loaded;
	}

	get fetching() {
		return this._fetching;
	}

	get paused() {
		return this._paused;
	}

	get ended() {
		return this._ended;
	}

	get basePdt() {
		return this._hlsJsController.basePdt;
	}

	get buffering() {
		return this._buffering;
	}

	get seeking() {
		return this._seeking;
	}

	get live() {
		return this._hlsJsController.live;
	}

	get slidingWindow() {
		return this._hlsJsController.slidingWindow;
	}

	get audioOnly() {
		return this._hlsJsController.audioOnly;
	}

	get multithreaded() {
		return this._hlsJsController.multithreaded;
	}

	get duration() {
		return this._mediaController.duration;
	}

	get variants() {
		return this._hlsJsController.levels;
	}

	get audioRenditions() {
		return this._hlsJsController.audioRenditions;
	}

	get currentAudioRendition() {
		return this._hlsJsController.currentAudioRendition;
	}

	set currentAudioRendition(index) {
		this._hlsJsController.currentAudioRendition = index;
	}

	get initialAudioLanguage() {
		return this._hlsJsController.initialAudioLanguage;
	}

	set initialAudioLanguage(language) {
		this._hlsJsController.initialAudioLanguage = language;
	}

	get initialAudioName() {
		return this._hlsJsController.initialAudioName;
	}

	set initialAudioName(name) {
		this._hlsJsController.initialAudioName = name;
	}

	get initialAudioIndex() {
		return this._hlsJsController.initialAudioIndex;
	}

	set initialAudioIndex(index) {
		this._hlsJsController.initialAudioIndex = index;
	}


	get subtitleRenditions() {
		return this._hlsJsController.subtitleRenditions;
	}

	get subtitlesEnabled() {
		return this._mediaController.subtitlesEnabled;
	}

	set subtitlesEnabled(value) {
		this._mediaController.subtitlesEnabled = value;
	}

	set changeSubtitlesByIndex(value) {
		this._hlsJsController.changeSubtitlesByIndex = value;
	}

	get muted() {
		return this._mediaController.muted;
	}

	set muted(value) {
		this._mediaController.muted = value;
	}

	get volume() {
		return this._mediaController.volume;
	}

	set volume(value) {
		this._mediaController.volume = value;
	}

	get debug() {
		return this._debug;
	}

	get disposed() {
		return this._disposed;
	}

	set debug(value) {
		this._debug = !!value;
		enableLogs(this._debug);
	}

	getConstraints() {
		return this._hlsJsController.getConstraints();
	}

	setConstraints(constraints) {
		let causeNoSideEffects = false;

		if (this._mediaState === MediaState.DETACHED || this._state === State.UNLOADED) {
			causeNoSideEffects = true;
		}

		this._hlsJsController.setConstraints(constraints, causeNoSideEffects);
	}

	getTimeouts() {
		return this._hlsJsController.getTimeouts();
	}

	setTimeouts(timeouts) {
		this._hlsJsController.setTimeouts(timeouts);
	}

	get useAutomaticComplexityReduction() {
		return this._hlsJsController.useAutomaticComplexityReduction;
	}

	set useAutomaticComplexityReduction(value) {
		this._hlsJsController.useAutomaticComplexityReduction = value;
	}

	get bitrateCycle() {
		return this._hlsJsController.bitrateCycle;
	}

	set bitrateCycle(value) {
		this._hlsJsController.bitrateCycle = value;
	}

	get maximumBufferSize() {
		return this._hlsJsController.maximumBufferSize;
	}

	set maximumBufferSize(value) {
		this._hlsJsController.maximumBufferSize = value;
	}

	get minimumBufferLength() {
		return this._hlsJsController.minimumBufferLength;
	}

	set minimumBufferLength(value) {
		this._hlsJsController.minimumBufferLength = value;
	}

	get maximumBufferLength() {
		return this._hlsJsController.maximumBufferLength;
	}

	set maximumBufferLength(value) {
		this._hlsJsController.maximumBufferLength = value;
	}

	get startTime() {
		return this._hlsJsController.startTime;
	}

	set startTime(seconds) {
		this._hlsJsController.startTime = seconds;
	}

	get startTimeByPdt() {
		return this._hlsJsController.startTimeByPdt;
	}

	set startTimeByPdt(time) {
		this._hlsJsController.startTimeByPdt = time;
	}

	get startVariant() {
		return this._hlsJsController.startLevel;
	}

	set startVariant(variant) {
		this._hlsJsController.startLevel = variant;
	}

	get startBitrate() {
		return this._hlsJsController.startBitrate;
	}

	set startBitrate(bitrate) {
		this._hlsJsController.startBitrate = bitrate;
	}

	get currentTime() {
		return this._mediaController.currentTime;
	}

	set currentTime(seconds) {
		logger.log( `set currentTime: seconds: ${seconds}, _seekableRange.start: ${this._seekableRange.start} liveTime: ${this.liveTime}`);
		Utils.checkNotFiniteAndThrow(seconds, 'currentTime');
		//we don't need both set currentTime and seek() to give warnings
		//Utils.checkLessThanZeroPointZeroAndWarn(seconds, 'currentTime');

		this.seek(seconds);
	}

	get currentTimeAsPdt() {
		return this.getPdtForTime(this.currentTime);
	}

	set currentTimeAsPdt(pdt) {
		this.seekByPdt(pdt);
		//this.currentTime = this.getTimeForPdt(pdt);
	}

	get playbackRate() {
		return this._mediaController.playbackRate;
	}

	set playbackRate(value) {
		if (this.live) {
			if(this.currentTime > this.liveTime && value > 1) {
				logger.warn(`set playbackRate: can't fast forward after the live point`);
			}
			else if(this.currentTime < this.seekableRange.start && value < 1) {
				logger.warn(`set playbackRate: can't slow forward before the start of the sliding window`);
			}
			else {
				this._mediaController.playbackRate = value;
			}
		}
		else {
			this._mediaController.playbackRate = value;
		}
	}

	get initialTime() {
		return this._initialTime !== -1 ? this._initialTime : 0;
	}

	get liveTime() {
		return this._hlsJsController.liveTime;
	}

	get seekableRange() {
		let seekableRange = {
			start: this._seekableRange.start !== undefined ? this._seekableRange.start : 0,
			end: this._seekableRange.end !== undefined ? this._seekableRange.end : 0,
			duration: this._seekableRange.duration !== undefined ? this._seekableRange.duration : 0,
		};

		return seekableRange;
	}

	get bufferedRange() {
		return this._hlsJsController.bufferedRange;
	}

	get playbackRange() {
		let playbackRange = {
			start: this._playbackRange.start !== undefined ? this._playbackRange.start : 0,
			end: this._playbackRange.end !== undefined ? this._playbackRange.end : 0,
			duration: this._playbackRange.duration !== undefined ? this._playbackRange.duration : 0,
		};

		return playbackRange;
	}

	//while bufferedRange is calculated in HlsJsController, we want to calculate bufferedRangeCurrent here, due to
	//easier acces to currentTime
	get bufferedRangeCurrent() {
		for(let i = 0; i < this.bufferedRange.length; i++) {
			if(this.currentTime >= this.bufferedRange[i].start && this.currentTime <= this.bufferedRange[i].end) {
				return this.bufferedRange[i];
			}
		}

		return { start: this.currentTime, end: this.currentTime, duration: 0 };
	}

	get instanceId() {
		return this._instanceId;
	}

	/**
	* return a HlsPlaybackMetrics structure
	*
	* https://jira.mlbam.com/browse/BAMHLS-309
	*
	* From Primetime TVSDK API PlaybackMetrics:
	* - bitrate:Number;
	* - bufferLength:Number;
	* - bufferTime:Number;
	* - droppedFramesCount:Number;
	* - frameRate:Number;
	* - readHead:Number;
	* - time:Number;
	*
	* - bufferedRange:VideoTimeRange;
	* - playbackRange:VideoTimeRange;
	* - seekableRange:VideoTimeRange;
	*
	* @returns {HlsPlaybackMetrics}
	  */
	//TODO: change this so that it's a persistent object, like network metrics
	//the base of it is already there, for bitrate, it just needs to be expanded
	getPlaybackMetrics() {
		let metrics = {};

		metrics.bitrate = this._playbackMetrics.bitrate;
		metrics.droppedFramesCount = this._mediaController.droppedFramesCount;
		metrics.frameRate = this._mediaController.frameRate;
		metrics.time = this.currentTime;
		//
		metrics.bufferedRange = this.bufferedRangeCurrent;
		metrics.playbackRange = this.playbackRange;
		metrics.seekableRange = this.seekableRange;

		return metrics;
	}

	getNetworkMetrics() {
		return this._hlsJsController.getNetworkMetrics();
	}

	getDrmCapabilities() {
		return this._hlsJsController.getDrmCapabilities();
	}

	_onNetworkMetricsChanged(data) {
		let lastMetrics = data.lastNetworkMetrics;
		let currentMetrics = data.networkMetrics;

		if(currentMetrics.chosenVariant !== lastMetrics.chosenVariant) {
			let eventData = { chosenVariant: currentMetrics.chosenVariant, lastChosenVariant:lastMetrics.chosenVariant };

			this._emit(Event.STREAM_CHOSENVARIANT_CHANGED, eventData);
		}

		if(currentMetrics.chosenBitrate !== lastMetrics.chosenBitrate) {
			this._playbackMetrics.bitrate = currentMetrics.chosenBitrate;

			let eventData = { chosenBitrate: currentMetrics.chosenBitrate, lastChosenBitrate:lastMetrics.chosenBitrate };

			this._emit(Event.STREAM_CHOSENBITRATE_CHANGED, eventData);
		}
	}

	_onTestEvent(data) {
		logger.log('TEST_EVENT received');
		this.testEventData = data;
	}

	fireTestEvent(data) {
		logger.log('firing TEST_EVENT');
		this._emit(Event.TEST_EVENT, data);
	}

	addUserMarker(time, callback, data) {
		return this._markers.addTypedMarker(time, callback, 'user', data);
	}


	removeMarker(markerId) {
		this._markers.removeMarker(markerId);
	}

	getTimeForPdt(pdt) {
		if(this.basePdt) {
			return this._hlsJsController.getTimeForPdt(pdt);
		}
		else {
			return undefined;
		}
	}

	getPdtForTime(time) {
		if(this.basePdt) {
			return this._hlsJsController.getPdtForTime(time);
		}
		else {
			return undefined;  //was null, but undefined is more consistent
		}
	}
}


export default HlsController;



// WEBPACK FOOTER //
// ./src/HlsController.js